import type { RefObject } from 'react';
import { seriesColorForVariant } from './control-options';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, Tooltip, XAxis, YAxis, Legend } from 'recharts';

/**
 * Chart furniture, defined once for the three charts in this folder.
 *
 * Recharts takes SVG presentation attributes rather than classes, so the tokens have
 * to be read as values here. They are the same values the dashboard's own chart uses:
 * a recessive grid and axis, and a tooltip that looks like a panel because it is one.
 * Colour for a series never appears in this file — that is `seriesColorForVariant` alone.
 */
export const AXIS_TICK = { fill: 'hsl(var(--dim))', fontSize: 10, fontFamily: 'var(--font-mono)' };

export const TOOLTIP_CONTENT_STYLE = {
  background: 'hsl(var(--panel))',
  border: '1px solid var(--line)',
  borderRadius: 12,
  fontSize: 12,
};

export const TOOLTIP_LABEL_STYLE = { color: 'hsl(var(--dim))', fontFamily: 'var(--font-mono)', fontSize: 10 };

export const TOOLTIP_ITEM_STYLE = { color: 'hsl(var(--text))', fontSize: 12 };

export const LEGEND_STYLE = { fontSize: 11, color: 'hsl(var(--dim))' };

export type ChartDataRaw = Record<string, { data: { date: string; value: number }[]; variantName: string }>;

export const processChartData = (chartDataRaw: ChartDataRaw | null, goal: string) => {
  if (!chartDataRaw) return { chartData: [], categories: [], variantIds: [] };

  const variants = Object.entries(chartDataRaw);
  if (variants.length === 0) return { chartData: [], categories: [], variantIds: [] };

  const allDates = new Set<string>();
  for (const [, variant] of variants) {
    for (const point of variant.data) {
      allDates.add(point.date);
    }
  }

  const sortedDates = Array.from(allDates).sort();

  const chartData = sortedDates.map((date) => {
    const point: Record<string, string | number> = { date };
    for (const [, variant] of variants) {
      const dataPoint = variant.data.find((d) => d.date === date);
      const value = dataPoint?.value || 0;
      point[variant.variantName] = goal === 'Revenue' ? value / 100 : value;
    }
    return point;
  });

  const categories = variants.map(([, v]) => v.variantName);
  // Parallel to `categories`: the colour is chosen by variant, not by position here.
  const variantIds = variants.map(([id]) => id);

  return { chartData, categories, variantIds };
};

type StatisticsChartProps = {
  chartDataRaw: ChartDataRaw | null;
  goal: string;
  goalLabel?: string;
  chartRef?: RefObject<HTMLDivElement | null>;
  /** Colour slots for the experiment, so every chart gives a variant the same colour. */
  colorOrder: readonly number[];
};

export const StatisticsChart = ({ chartDataRaw, goal, goalLabel, chartRef, colorOrder }: StatisticsChartProps) => {
  const { chartData, categories, variantIds } = processChartData(chartDataRaw, goal);

  if (chartData.length === 0) return null;

  return (
    <div ref={chartRef} className="border-line bg-panel2 rounded-card border">
      <div className="border-line border-b px-4 py-3">
        <h3 className="text-foreground text-title font-medium tracking-[-0.01em]">{goalLabel ?? goal} over time</h3>
        <p className="supporting">Daily {(goalLabel ?? goal).toLowerCase()} per variant throughout the experiment.</p>
      </div>

      <div className="p-4">
        <ResponsiveContainer width="100%" height={384}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
            <defs>
              {categories.map((category, idx) => (
                <linearGradient key={category} id={`color-${idx}`} x1="0" y1="0" x2="0" y2="1">
                  {/*
                    A quiet wash rather than a solid fill: with four or five variants on one
                    axis an opaque area hides the lines under it, and the line is what is
                    being read.
                  */}
                  <stop
                    offset="5%"
                    stopColor={seriesColorForVariant(variantIds[idx]!, colorOrder)}
                    stopOpacity={0.22}
                  />
                  <stop offset="95%" stopColor={seriesColorForVariant(variantIds[idx]!, colorOrder)} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            {/* Horizontal rules only, so the grid helps read a value without competing
                with the series. */}
            <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={AXIS_TICK}
              tickMargin={8}
              minTickGap={24}
              stroke="var(--line)"
              tickLine={false}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getDate()}/${date.getMonth() + 1}`;
              }}
            />
            <YAxis
              width={56}
              tick={AXIS_TICK}
              stroke="var(--line)"
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => {
                if (goal === 'Revenue') {
                  return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value.toFixed(0);
                }
                return value.toFixed(0);
              }}
            />
            <Tooltip
              cursor={{ stroke: 'var(--line)', strokeWidth: 1 }}
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
              formatter={(value: number | undefined, name: string | undefined) => {
                if (value === undefined) return [0, name ?? ''];
                if (goal === 'Revenue') {
                  return [
                    value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                    name ?? '',
                  ];
                }
                return [value, name ?? ''];
              }}
              labelFormatter={(label) => new Date(label).toLocaleDateString()}
            />
            <Legend wrapperStyle={LEGEND_STYLE} iconType="plainline" iconSize={12} />
            {categories.map((category, idx) => (
              <Area
                key={category}
                type="monotone"
                dataKey={category}
                stroke={seriesColorForVariant(variantIds[idx]!, colorOrder)}
                activeDot={{
                  r: 4,
                  stroke: seriesColorForVariant(variantIds[idx]!, colorOrder),
                  strokeWidth: 2,
                  fill: 'hsl(var(--panel))',
                }}
                fillOpacity={1}
                fill={`url(#color-${idx})`}
                strokeWidth={2}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
